interface ServiceMedia {
  type: "image" | "video"
  src: string
  poster?: string
  credit?: string
  alt?: string
}

interface ServiceCard {
  key: "touring" | "concerts" | "studio" | "screen_printing"
  title: string
  description: string
  media: ServiceMedia
}

interface ServicesProps {
  title: string
  subtitle: string
  learn_more: string
  cards: ServiceCard[]
}

export default function Services({
  title,
  subtitle,
  learn_more,
  cards,
}: ServicesProps) {
  const handleContactClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const target = document.getElementById("contact")
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  return (
    <section id="services" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-contrast mb-4">
            {title}
          </h2>
          <p className="text-content max-w-2xl mx-auto mb-6">{subtitle}</p>
          <div className="w-16 h-px bg-contrast mx-auto" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {cards.map((card) => (
            <div
              key={card.key}
              className="bg-light border border-contour hover:shadow-lg transition-shadow flex flex-col overflow-hidden"
            >
              <div className="relative aspect-[4/3] bg-contrast overflow-hidden">
                {card.media.type === "video" ? (
                  <video
                    className="w-full h-full object-cover"
                    src={card.media.src}
                    poster={card.media.poster}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={card.media.src}
                    alt={card.media.alt ?? card.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                )}
                {card.media.credit && (
                  <span className="absolute bottom-2 right-2 text-[10px] text-light/80 bg-contrast/60 px-2 py-1 tracking-wide">
                    {card.media.credit}
                  </span>
                )}
              </div>

              <div className="p-8 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold text-contrast mb-3">
                  {card.title}
                </h3>
                <p className="text-content flex-grow">{card.description}</p>

                <a
                  href="#contact"
                  onClick={handleContactClick}
                  className="mt-6 inline-flex items-center text-contrast font-medium hover:underline"
                >
                  {learn_more} →
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
